import { useMemo, useState } from 'react';
import type { CompetitionEvent, ResultRow } from '@/data/types';

/** Seconds → "mm:ss" (or "h:mm:ss" past an hour). */
function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

interface RankedRow extends ResultRow {
  rank: number | null;
}

/**
 * Sort finished teams by time (ties share a rank), then append non-finished
 * teams (no rank) at the bottom. Used per-category and for the Overall view.
 */
function rank(rows: ResultRow[]): RankedRow[] {
  const finished = rows
    .filter((r) => r.timeSeconds !== null)
    .sort((a, b) => (a.timeSeconds as number) - (b.timeSeconds as number));

  const ranked: RankedRow[] = [];
  let lastTime: number | null = null;
  let lastRank = 0;
  finished.forEach((r, i) => {
    if (r.timeSeconds !== lastTime) lastRank = i + 1;
    lastTime = r.timeSeconds;
    ranked.push({ ...r, rank: lastRank });
  });

  const unfinished = rows
    .filter((r) => r.timeSeconds === null)
    .map((r) => ({ ...r, rank: null }));

  return [...ranked, ...unfinished];
}

const MEDALS = ['🥇', '🥈', '🥉'];

export default function ResultsBoard({ event }: { event: CompetitionEvent }) {
  const timeLabel = event.resultLabel ?? 'Time';
  const [activeTab, setActiveTab] = useState('overall');
  const [query, setQuery] = useState('');

  // Build the tab list: Overall first, then each category.
  const tabs = useMemo(
    () => [
      { id: 'overall', name: 'Overall' },
      ...event.categories.map((c) => ({ id: c.id, name: c.name })),
    ],
    [event]
  );

  // Ranked rows for the active tab (overall flattens every category).
  const rankedRows = useMemo(() => {
    if (activeTab === 'overall') {
      const all = event.categories.flatMap((c) => c.results);
      return rank(all);
    }
    const cat = event.categories.find((c) => c.id === activeTab);
    return cat ? rank(cat.results) : [];
  }, [event, activeTab]);

  // Live search across both athlete names and bib.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rankedRows;
    return rankedRows.filter(
      (r) =>
        r.bib.toLowerCase().includes(q) ||
        r.athletes.some((a) => a.toLowerCase().includes(q)) ||
        (r.teamName?.toLowerCase().includes(q) ?? false)
    );
  }, [rankedRows, query]);

  return (
    <div>
      {/* Category tabs */}
      <div className="flex overflow-x-auto gap-2 mb-5 pb-2 scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer flex-shrink-0 ${
              activeTab === tab.id
                ? 'bg-[#14B8A6] text-white scale-105'
                : 'bg-[#1a1a1a] text-gray-400 hover:text-white hover:bg-[#252525] border border-white/[0.06]'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by athlete name or bib…"
          className="w-full bg-white/[0.05] border border-white/10 rounded-2xl pl-11 pr-4 py-3.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#14B8A6]/60 focus:bg-white/[0.07] transition-colors"
        />
      </div>

      {/* Results table */}
      <div className="bg-[#111111] rounded-3xl border border-white/[0.06] overflow-hidden">
        {/* Header row (hidden on mobile) */}
        <div className="hidden sm:grid grid-cols-[64px_1fr_auto] gap-4 px-6 py-3.5 border-b border-white/[0.06] text-xs uppercase tracking-wider text-gray-500 font-semibold">
          <span>Rank</span>
          <span>Athletes</span>
          <span className="text-right">{timeLabel}</span>
        </div>

        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="ri-search-eye-line text-3xl text-gray-600 mb-3" />
            <p className="text-gray-500 text-sm">No results match “{query}”.</p>
            <button
              onClick={() => setQuery('')}
              className="text-[#14B8A6] text-xs mt-2 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          rows.map((r) => {
            const medal = r.rank !== null && r.rank <= 3 ? MEDALS[r.rank - 1] : null;
            return (
              <div
                key={r.bib}
                className="grid grid-cols-[44px_1fr_auto] sm:grid-cols-[64px_1fr_auto] gap-3 sm:gap-4 px-4 sm:px-6 py-3.5 border-b border-white/[0.04] last:border-0 items-center hover:bg-white/[0.02] transition-colors"
              >
                {/* Rank */}
                <span className="text-base sm:text-lg font-bold tabular-nums">
                  {medal ? (
                    <span className="text-xl">{medal}</span>
                  ) : r.rank !== null ? (
                    <span className="text-gray-300">{r.rank}</span>
                  ) : (
                    <span className="text-gray-600 text-sm">—</span>
                  )}
                </span>

                {/* Athletes */}
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium leading-snug">
                    <div>
                      {r.athletes[0]} <span className="text-gray-600">&amp;</span>
                    </div>
                    <div>{r.athletes[1]}</div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-600 text-xs font-mono">{r.bib}</span>
                    {r.timeSeconds === null && (
                      <span className="text-amber-400/80 text-[10px] font-semibold uppercase tracking-wide bg-amber-400/10 border border-amber-400/20 px-1.5 py-0.5 rounded">
                        {r.status}
                      </span>
                    )}
                  </div>
                </div>

                {/* Time */}
                <span className="text-right font-mono text-sm sm:text-base text-[#14B8A6] tabular-nums">
                  {r.timeSeconds !== null ? formatTime(r.timeSeconds) : '—'}
                </span>
              </div>
            );
          })
        )}
      </div>

      <p className="text-gray-600 text-xs mt-4 text-center">
        {rows.length} team{rows.length === 1 ? '' : 's'}
        {query ? ' matching your search' : ` in ${tabs.find((t) => t.id === activeTab)?.name}`}.
      </p>
    </div>
  );
}
